import React, { useState, useEffect } from 'react';
import { db, guestService } from '../../services/db';
import RsvpForm from '../RsvpForm';
import './RsvpButtonElement.css';

function RsvpButtonElement({ element, isPreviewMode, invitationId, eventId, onUpdate }) {
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  
  // Log props on mount to check if they're defined
  useEffect(() => {
    console.log('RsvpButtonElement mounted with props:', { 
      isPreviewMode, 
      invitationId: invitationId || 'undefined', 
      eventId: eventId || 'undefined',
      element
    });
  }, [isPreviewMode, invitationId, eventId, element]);
  
  // Add effect to handle body scroll lock when modal is open
  useEffect(() => {
    if (showForm) {
      // Lock scrolling on the body when modal is open
      document.body.style.overflow = 'hidden';
      console.log('Modal opened, locking body scroll');
    } else {
      // Restore scrolling when modal is closed
      document.body.style.overflow = '';
      console.log('Modal closed, restoring body scroll');
    }
    
    // Cleanup function to ensure scroll is restored
    return () => {
      document.body.style.overflow = '';
    };
  }, [showForm]);
  
  const handleButtonClick = (e) => {
    console.log('*** RSVP BUTTON CLICKED ***');
    
    // Only prevent default but allow bubbling in edit mode
    if (e) {
      e.preventDefault();
      // Only stop propagation in preview mode
      if (isPreviewMode) {
        e.stopPropagation();
      }
    }
    
    // Only show the form when in preview mode
    if (isPreviewMode) {
      setShowForm(true);
      setFormError(''); // Clear any previous errors
      console.log('Updated showForm state to true');
    } else {
      // If not in preview mode, show notification or select the element for editing
      console.log('Button clicked in edit mode - selecting for editing');
      // If onUpdate is available, we can select this element
      if (onUpdate) {
        // Notify that button will only work in preview mode
        const notification = document.createElement('div');
        notification.className = 'edit-mode-notification';
        notification.textContent = 'Button will only work in preview mode';
        document.body.appendChild(notification);
        
        setTimeout(() => {
          document.body.removeChild(notification);
        }, 2000);
      }
    }
  };
  
  const handleSubmit = async (formData) => {
    console.log('Form submitted with data:', formData);
    console.log('Current context:', { isPreviewMode, invitationId, eventId });
    
    try {
      // In preview mode (published view), save to database
      if (isPreviewMode) {
        // Check if we have the required IDs before submitting
        if (!eventId) {
          console.error('Cannot submit RSVP: eventId is undefined');
          setFormError('Error: Event ID is missing. Please try again later or contact support.');
          return;
        }
        
        if (!invitationId) {
          console.error('Cannot submit RSVP: invitationId is undefined');
          setFormError('Error: Invitation ID is missing. Please try again later or contact support.');
          return;
        }
        
        console.log('Saving RSVP to database with IDs:', { eventId, invitationId });
        
        // Save RSVP to database
        await guestService.create({
          eventId,
          invitationId,
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email || null,
          phone: formData.phone || null,
          guestCount: formData.guestCount,
          rsvpStatus: 'attending',
          message: formData.message || '',
          submittedAt: new Date().toISOString()
        });
        console.log('RSVP saved successfully');
      } else {
        console.log('In edit mode, not saving to database (simulating successful submission)');
      }
      
      setSubmitted(true);
      setFormError('');
      setTimeout(() => {
        setShowForm(false);
        setSubmitted(false); // Reset for next time
      }, 3000);
    } catch (error) {
      console.error("Error saving RSVP:", error);
      setFormError("There was an error submitting your RSVP. Please try again.");
    }
  };
  
  const { 
    backgroundColor = '#4caf50',
    textColor = 'white',
    text = 'RSVP Now',
    borderRadius = '4px',
    fontSize = '16px',
    fontWeight = 'bold',
    width = '200px',
    height = '50px',
    boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)',
    buttonStyle: styleType = 'filled'
  } = element.style || {};

  // Get button style based on button type
  const getButtonStyles = () => {
    const baseStyle = {
      width,
      height,
      borderRadius,
      fontSize,
      fontWeight,
      color: textColor,
      // Add a specific cursor style based on mode
      cursor: isPreviewMode ? 'pointer' : 'default'
    };

    switch (styleType) {
      case 'outlined':
        return {
          ...baseStyle,
          backgroundColor: 'transparent',
          border: `2px solid ${backgroundColor}`,
          color: backgroundColor,
          boxShadow: 'none'
        };
      case 'text':
        return {
          ...baseStyle,
          backgroundColor: 'transparent',
          border: 'none',
          color: backgroundColor,
          boxShadow: 'none'
        };
      case 'filled':
      default:
        return {
          ...baseStyle,
          backgroundColor,
          border: 'none',
          boxShadow
        };
    }
  };

  const buttonStyles = getButtonStyles();

  // Debug render state
  console.log('RsvpButtonElement rendering. showForm:', showForm, 'submitted:', submitted);

  // Log for each render to see if the component is re-rendering properly
  console.log('RsvpButtonElement render cycle complete -', new Date().toISOString());

  return (
    <>
      {!isPreviewMode && (
        <div className="button-edit-hint">
          Button will be functional in preview mode
        </div>
      )}
      <button 
        className={`rsvp-button-element ${!isPreviewMode ? 'edit-mode' : ''}`}
        style={buttonStyles}
        onClick={handleButtonClick}
        type="button"
      >
        {text}
      </button>
      
      {/* Simple modal without portal - only shown in preview mode */}
      {showForm && isPreviewMode && (
        <div className="rsvp-form-modal-overlay">
          <div className="rsvp-form-modal">
            {!submitted ? (
              <>
                <div 
                  className="rsvp-form-modal-close" 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('Close button clicked');
                    setShowForm(false);
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                
                {formError && (
                  <div className="rsvp-form-error">
                    <p>{formError}</p>
                  </div>
                )}
                
                <RsvpForm 
                  onSubmit={handleSubmit} 
                  disabled={!isPreviewMode} 
                />
              </>
            ) : (
              <div className="rsvp-success">
                <h3>Thank You!</h3>
                <p>Your RSVP has been submitted successfully.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default RsvpButtonElement; 